import { ConsoleResult, ConsoleSiteContext, ConsoleTranscriptEntry } from './types';

const HISTORY_KEY = 'website-command-console.history';
const TARGETS_KEY = 'website-command-console.targets';
const SERVERS_KEY = 'website-command-console.servers';
const OWNERS_KEY = 'website-command-console.owners';
const CONTEXT_KEY = 'website-command-console.context';
const TRANSCRIPT_KEY = 'website-command-console.transcript';
const MAX_HISTORY = 40;
const MAX_TARGETS = 20;
const MAX_SERVERS = 20;
const MAX_OWNERS = 20;
const MAX_TRANSCRIPT = 40;

function readJsonArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item) => typeof item === 'string' && item.trim().length > 0);
  } catch (_e) {
    return [];
  }
}

function writeJsonArray(key: string, values: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(values));
  } catch (_e) {
    // Ignore quota / private-mode failures.
  }
}

function pushUnique(key: string, value: string, max: number): string[] {
  const trimmed = value.trim();
  if (!trimmed) {
    return readJsonArray(key);
  }
  const next = [trimmed, ...readJsonArray(key).filter((item) => item !== trimmed)].slice(0, max);
  writeJsonArray(key, next);
  return next;
}

export function loadCommandHistory(): string[] {
  return readJsonArray(HISTORY_KEY);
}

export function pushCommandHistory(command: string): string[] {
  return pushUnique(HISTORY_KEY, command, MAX_HISTORY);
}

export function loadRecentTargets(): string[] {
  return readJsonArray(TARGETS_KEY);
}

export function pushRecentTarget(target: string): string[] {
  return pushUnique(TARGETS_KEY, target, MAX_TARGETS);
}

/** Prefer domain; drop matching raw numeric ID when both refer to the same site. */
export function rememberWebsiteTarget(domain: string, id: number): void {
  const idStr = String(id);
  const current = loadRecentTargets().filter((item) => item !== idStr && item !== domain);
  const next = [domain, ...current].slice(0, MAX_TARGETS);
  writeJsonArray(TARGETS_KEY, next);
}

export function loadRecentServers(): string[] {
  return readJsonArray(SERVERS_KEY);
}

export function pushRecentServer(server: string): string[] {
  return pushUnique(SERVERS_KEY, server, MAX_SERVERS);
}

export function loadRecentOwners(): string[] {
  return readJsonArray(OWNERS_KEY);
}

export function pushRecentOwner(owner: string): string[] {
  return pushUnique(OWNERS_KEY, owner, MAX_OWNERS);
}

export function loadSiteContext(): ConsoleSiteContext | null {
  try {
    const raw = localStorage.getItem(CONTEXT_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (typeof parsed.id !== 'number' || typeof parsed.domain !== 'string') {
      return null;
    }
    return {
      id: parsed.id,
      domain: parsed.domain,
      status: typeof parsed.status === 'string' ? parsed.status : '',
      originServer: typeof parsed.originServer === 'string' ? parsed.originServer : '',
    };
  } catch (_e) {
    return null;
  }
}

export function saveSiteContext(context: ConsoleSiteContext | null): void {
  try {
    if (!context) {
      localStorage.removeItem(CONTEXT_KEY);
      return;
    }
    localStorage.setItem(CONTEXT_KEY, JSON.stringify(context));
  } catch (_e) {
    // Ignore.
  }
}

export function contextFromWebsite(website: {
  id: number;
  domain: string;
  status: string;
  originServer: string;
  userId?: string;
}): ConsoleSiteContext {
  if (website.userId) {
    pushRecentOwner(website.userId);
  }
  if (website.originServer) {
    pushRecentServer(website.originServer);
  }
  rememberWebsiteTarget(website.domain, website.id);
  return {
    id: website.id,
    domain: website.domain,
    status: website.status,
    originServer: website.originServer,
  };
}

function sanitizeResult(result: ConsoleResult | null): ConsoleResult | null {
  if (!result) {
    return null;
  }

  if (result.kind === 'logs' && result.pending) {
    return {
      ...result,
      pending: false,
      timedOut: true,
      logs: result.logs || [],
    };
  }

  if (result.kind === 'action' && result.phase === 'pending') {
    return {
      ...result,
      phase: 'form',
      message: result.message || 'Interrupted — review and submit again if needed.',
      errorMessage: undefined,
    };
  }

  return result;
}

function sanitizeEntry(entry: any): ConsoleTranscriptEntry | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  if (typeof entry.id !== 'string' || typeof entry.input !== 'string') {
    return null;
  }
  return {
    id: entry.id,
    input: entry.input,
    createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : '',
    loading: false,
    busy: false,
    result: sanitizeResult(entry.result || null),
  };
}

export function loadTranscript(): ConsoleTranscriptEntry[] {
  try {
    const raw = localStorage.getItem(TRANSCRIPT_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map(sanitizeEntry)
      .filter((entry: ConsoleTranscriptEntry | null): entry is ConsoleTranscriptEntry => !!entry)
      .slice(-MAX_TRANSCRIPT);
  } catch (_e) {
    return [];
  }
}

export function saveTranscript(entries: ConsoleTranscriptEntry[]): void {
  try {
    const sanitized = (entries || [])
      .map(sanitizeEntry)
      .filter((entry: ConsoleTranscriptEntry | null): entry is ConsoleTranscriptEntry => !!entry)
      .slice(-MAX_TRANSCRIPT);
    localStorage.setItem(TRANSCRIPT_KEY, JSON.stringify(sanitized));
  } catch (_e) {
    // Ignore quota / private-mode failures; try a smaller payload once.
    try {
      const trimmed = (entries || []).slice(-10).map(sanitizeEntry)
        .filter((entry: ConsoleTranscriptEntry | null): entry is ConsoleTranscriptEntry => !!entry);
      localStorage.setItem(TRANSCRIPT_KEY, JSON.stringify(trimmed));
    } catch (_e2) {
      // Ignore.
    }
  }
}

export function clearTranscriptStorage(): void {
  try {
    localStorage.removeItem(TRANSCRIPT_KEY);
  } catch (_e) {
    // Ignore.
  }
}
